/**
 * Rendering Algolia's highlighted snippets without handing it an HTML hole.
 *
 * Algolia returns matches as a string with the matched runs wrapped in the
 * configured tags — `<mark>` here — and everything else HTML-escaped. The
 * obvious way to render that is dangerouslySetInnerHTML, and the obvious way
 * is wrong: the string is built from POLICY CONTENT, which in this product is
 * uploaded by users, so trusting it is trusting whatever an author pasted into
 * a document. Algolia's escaping is not a security boundary we control, and
 * the failure mode is script execution in a hospital's browser.
 *
 * So the string is parsed into plain segments instead, and the component
 * renders real elements. Nothing that arrives from the index is ever
 * interpreted as markup.
 */

export interface Segment {
  text: string;
  marked: boolean;
}

const OPEN = '<mark>';
const CLOSE = '</mark>';

/**
 * Undo the escaping Algolia applied to the surrounding text.
 *
 * Ordered so that `&amp;` is handled LAST. Reversed, "&amp;lt;" would decode
 * to "&lt;" and then to "<", reintroducing exactly the character the escaping
 * existed to neutralize.
 */
function unescapeHtml(value: string): string {
  return value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
}

/**
 * Split a highlighted value into marked and unmarked runs.
 *
 * Unbalanced or stray tags are treated as text rather than throwing: a snippet
 * that renders slightly wrong beats a result list that crashes, and this input
 * comes from a third-party service on somebody else's release schedule.
 */
export function parseHighlight(value: string | undefined | null): Segment[] {
  if (!value) return [];

  const segments: Segment[] = [];
  let rest = value;

  while (rest.length > 0) {
    const open = rest.indexOf(OPEN);
    if (open === -1) break;

    const close = rest.indexOf(CLOSE, open + OPEN.length);
    if (close === -1) break;

    if (open > 0) segments.push({ text: unescapeHtml(rest.slice(0, open)), marked: false });
    segments.push({
      text: unescapeHtml(rest.slice(open + OPEN.length, close)),
      marked: true,
    });
    rest = rest.slice(close + CLOSE.length);
  }

  if (rest.length > 0) segments.push({ text: unescapeHtml(rest), marked: false });

  return segments.filter((s) => s.text.length > 0);
}

/** Whether a highlighted value actually matched anything. */
export function hasMatch(value: string | undefined | null): boolean {
  return Boolean(value && value.includes(OPEN));
}

/**
 * Strip the markup a snippet carries out of the stored document.
 *
 * Policy bodies are Markdown, and Algolia snippets the source, so a match near
 * a heading arrives as "## Purpose This policy establishes ## Scope Applies
 * to". Rendered verbatim that reads as corrupted text, and it is the first
 * thing anyone notices about the results page.
 *
 * Safe to run before parseHighlight: `<mark>` contains none of the characters
 * removed here, so the tags survive intact.
 */
export function tidySnippet(value: string): string {
  return value
    // ATX headings, at a line start or after the ellipsis Algolia inserts.
    .replace(/(^|[\n\r])\s*#{1,6}\s*/g, '$1')
    // Bold and italic runs, which carry no meaning without the styling.
    .replace(/\*\*|__/g, '')
    // List bullets and blockquote markers at a line start.
    .replace(/(^|[\n\r])\s*[-*+>]\s+/g, '$1')
    // Everything becomes one line: a snippet is a fragment, not a document.
    .replace(/\s*[\n\r]+\s*/g, ' ')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

/** The snippet to show under a hit: the first field that actually matched. */
export function bestSnippet(hit: {
  _snippetResult?: {
    summary?: { value: string };
    editorMarkdownText?: { value: string };
    attachmentText?: { value: string };
  };
  summary?: string;
}): { value: string; source: 'summary' | 'content' | 'attachment' | 'none' } {
  const s = hit._snippetResult;

  // Body text first: if the words were found inside the document, showing the
  // summary instead leaves the reader wondering why the policy came up.
  if (hasMatch(s?.editorMarkdownText?.value)) {
    return { value: tidySnippet(s!.editorMarkdownText!.value), source: 'content' };
  }
  if (hasMatch(s?.attachmentText?.value)) {
    return { value: tidySnippet(s!.attachmentText!.value), source: 'attachment' };
  }
  if (hasMatch(s?.summary?.value)) {
    return { value: tidySnippet(s!.summary!.value), source: 'summary' };
  }
  // Nothing matched in the body, so the match was in the title or a tag. Fall
  // back to the plain summary for context rather than showing nothing.
  if (s?.summary?.value) return { value: tidySnippet(s.summary.value), source: 'summary' };
  return { value: hit.summary ? tidySnippet(hit.summary) : '', source: 'none' };
}
